import { IWorkspaceVariantsDetails } from "../../types/WorkspaceVariantsDetails.types";
import {
  IDraftQuoteRequest,
  IDraftQuoteResponse,
} from "../interfaces/draft-quote.types";
import {
  IDynamicConfigRequest,
  IDynamicConfigResponse,
} from "../interfaces/dynamic-config.types";
import {
  BOOKING_DETAIL_URL,
  CANCELLATION_CHARGES_URL,
  CREATE_BOOKING_URL,
  CREATE_ORDER_URL,
  CREATE_QUOTE_URL,
  DRAFT_QUOTE_URL,
  DYNAMIC_CONFIG_URL,
  FINANCE_PROVIDER_URL,
  MMV_DEAL_URL,
  PAYMENT_RESPONSE_URL,
  WORKSPACE_MASTER_CARS_URL,
} from "./endpoints";

export interface IDealResponse {
  success: boolean;
  id: number;
  inventory_id: number;
  variant_color_id: number;

  brand: string;
  brand_id: number;
  model: string;
  model_id: number;
  variant: string;
  variant_id: number;
  body_type: string;
  color_type: string;
  color_name: string;
  base_color_list: string[];
  hexcode: string[];
  model_year: number;
  transmission: string;
  fuel_type: string;
  hero_image: string;
  dealer_id: number;
  city_id: number;
  booking_amount: number;
  market_price: number;
  market_price_attributes: MarketPriceAttributes;
  market_price_optional_attributes: MarketPriceOptionalAttributes;
  final_price: number;
  savings: number;
  waiting_period: string;
  kind_offer: string;
  savings_breakup: SavingsBreakup;
  hot_deal_data: HotDealData;
  is_car_finance_active: boolean;
}

interface HotDealData {
  is_hot_deal: boolean;
  price_difference: number;
}

interface MarketPriceAttributes {
  fast_tag: number;
  mcd_charges: number;
  auto_card: number;
  ex_showroom: number;
  registration_amount: number;
  dealer_insurance_premium: number;
}

interface MarketPriceOptionalAttributes {
  basic_accessories: number;
  hypothecation_charges: number;
}

interface SavingsBreakup {
  consumerdiscount: number;
  ripdiscount: number;
  newyear_discount: number;
  special_discount: number;
  ackodiscount: number;
}

export interface IFinanceProvider {
  id: number;
  provider_name: string;
  logo_image: string;
  interest_rate: string;
  is_active: boolean;
}

export interface IFinanceProvidersResponse {
  finance_provider: IFinanceProvider[];
}

export interface ICancellationChargeData {
  is_allowed: boolean;
  customer_stage: string;
  suggested_charges: string;
  delivery_type: string;
}

export interface ICancellationChargesResponse {
  success: boolean;
  data: ICancellationChargeData[];
}

interface IGlobalResponseType<T> {
  success: boolean;
  data: T;
}

export const fetchDeal = async (payload: {
  deal_type: string;
  inventory_id?: string;
  city_id: string;
  variant_color_id?: string;
}): Promise<IGlobalResponseType<IDealResponse | null>> => {
  try {
    const params: Record<string, string> = {
      deal_type: payload.deal_type,
      city_id: payload.city_id,
    };

    if (payload.inventory_id) {
      params.inventory_id = payload.inventory_id;
    }

    if (payload.variant_color_id) {
      params.variant_color_id = payload.variant_color_id;
    }

    const queryParams = new URLSearchParams(params).toString();

    const response = await fetch(`${MMV_DEAL_URL}?${queryParams}`, {
      method: 'GET',
    });

    const data = await response.json();

    return {
      success: data.success,
      data: data as IDealResponse,
    };
  } catch (error) {
    console.error(
      "[Booking Service] Error fetching deal by inventory ID:",
      error
    );
    return {
      success: false,
      data: null,
    };
  }
};

export const fetchVariantDetails = async (payload: {
  slug: string;
  city_id: string;
  token: string;
  variant_color_name: string;
}): Promise<IGlobalResponseType<IWorkspaceVariantsDetails | null>> => {
  try {
    let params;
    if(payload.variant_color_name){
      params = {
        slug: payload.slug,
        city_id: payload.city_id,
        variant_color_name: payload.variant_color_name
      };
    } else {
       params = {
        slug: payload.slug,
        city_id: payload.city_id,
      };
    }

    const queryParams = new URLSearchParams(params).toString();

    const response = await fetch(`${WORKSPACE_MASTER_CARS_URL}?${queryParams}`, {
      method: 'GET',
      headers: {
        retailtoken: payload.token,
      },
    });

    const data = await response.json();

    return {
      success: data.success,
      data: data as IWorkspaceVariantsDetails,
    };
  } catch (error) {
    console.error("[Booking Service] Error fetching variant details:", error);
    return {
      success: false,
      data: null,
    };
  }
};

export const fetchFinanceProviders = async (): Promise<
  IGlobalResponseType<IFinanceProvidersResponse | null>
> => {
  try {
    const response = await fetch(FINANCE_PROVIDER_URL, {
      method: 'GET',
    });

    const data = await response.json();

    return {
      success: data.success,
      data: data as any,
    };
  } catch (error) {
    console.error("[Booking Service] Error fetching finance providers:", error);
    return {
      success: false,
      data: null,
    };
  }
};

export const fetchCancellationCharges = async (
  deal_type?: string
): Promise<IGlobalResponseType<ICancellationChargesResponse["data"] | null>> => {
  try {
    const response = await fetch(CANCELLATION_CHARGES_URL, {
      method: 'GET',
    });

    const data = await response.json();

    let charges = data.data as ICancellationChargesResponse["data"];

    if (deal_type) {
      charges = charges.filter(
        (charge) => charge.delivery_type === deal_type
      );
    }

    return {
      success: true,
      data: charges,
    };
  } catch (error) {
    console.error(
      "[Booking Service] Error fetching cancellation charges:",
      error
    );
    return {
      success: false,
      data: null,
    };
  }
};

export const createQuote = async (
  payload: any,
  token: any
): Promise<any> => {
  try {

    const customHeaders: Record<string, string> = {
        "Content-Type": "application/json",
    };
    if (!token) {
        return { success: false, data: null };
    }
    customHeaders["retailtoken"] = token;

    const response = await fetch(CREATE_QUOTE_URL, {
      method: 'POST',
      headers: customHeaders,
      body: JSON.stringify(payload)
    });
    
    const data = await response.json();

     return {
      success: true,
      data: data,
    };
  } catch (error) {
    console.error("[Booking Service] Error creating quote:", error);
    return {
      success: false,
      data: null,
    };
  }
};

export const draftQuote = async (
  payload: any,
  token: any
): Promise<any> => {
  try {

    const customHeaders: Record<string, string> = {
        "Content-Type": "application/json",
    };
    if (!token) {
        return { success: false, data: null };
    }
    customHeaders["retailtoken"] = token;

    const queryParams = new URLSearchParams(payload).toString();

    const response = await fetch(`${DRAFT_QUOTE_URL}?${queryParams}`, {
      headers: customHeaders,
      method: 'GET',
    });
    
    const data = await response.json();

     return {
      success: true,
      data: data,
    };
  } catch (error) {
    console.error("[Booking Service] Error getting draft quote:", error);
    return {
      success: false,
      data: null,
    };
  }
};

export const fetchDynamicConfig = async (
  payload: IDynamicConfigRequest
): Promise<IGlobalResponseType<IDynamicConfigResponse | null>> => {
  try {
    const params: Record<string, string> = {
      key: payload.key,
      group: payload.group,
    };

    const queryParams = new URLSearchParams(params).toString();

    const response = await fetch(`${DYNAMIC_CONFIG_URL}?${queryParams}`, {
      method: 'GET',
    });

     if (!response) {
      console.error("fetchDynamicConfig fetch unsuccessful");
    }

    const data = await response.json();

    return {
      success: true,
      data: data as IDynamicConfigResponse,
    };
  } catch (error) {
    console.error("[Booking Service] Error fetching dynamic config:", error);
    return {
      success: false,
      data: null,
    };
  }
};

export const createBooking = async (
  payload: any,
  token: string
): Promise<any> => {
  try {

     const customHeaders: Record<string, string> = {
        "Content-Type": "application/json",
    };

    customHeaders["retailtoken"] = token;

    const response = await fetch(CREATE_BOOKING_URL, {
      method: 'POST',
      headers: customHeaders,
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    return {
      success: true,
      data: data,
    };
  } catch (error) {
    console.error("[Booking Service] Error creating booking:", error);
    return {
      success: false,
      data: null,
    };
  }
}

export const createOrder = async (
  payload: any,
  token: string
): Promise<any> => {
  try {
    const customHeaders: Record<string, string> = {
      "Content-Type": "application/json",
    };

    customHeaders["retailtoken"] = token;

    const response = await fetch(CREATE_ORDER_URL, {
      method: 'POST',
      headers: customHeaders,
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    return {
      success: true,
      data: data,
    };
  } catch (error) {
    console.error("[Booking Service] Error creating order:", error);
    return;
  }
}

export const getBookingDetail = async (
  ekey: string,
  token: string
): Promise<any> => {
  try {
    const customHeaders: Record<string, string> = {
      "Content-Type": "application/json",
    };

    customHeaders["retailtoken"] = token;

    const response = await fetch(`${BOOKING_DETAIL_URL}/${ekey}`, {
      method: 'GET',
      headers: customHeaders,
    });

    const data = await response.json();

    return {
      success: true,
      data: data,
    };
  } catch (error) {
    console.error("[Booking Service] Error fetching booking detail:", error);
    return {
      success: false,
      data: null,
    };
  }
}

export const getPaymentResponse = async (
  orderId: string,
  token: string
): Promise<any> => {
  try {
    const customHeaders: Record<string, string> = {
      "Content-Type": "application/json",
    };

    customHeaders["retailtoken"] = token;

    const payload = {
      order_id: orderId,
    };

    const response = await fetch(PAYMENT_RESPONSE_URL, {
      method: 'POST',
      headers: customHeaders,
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    return {
      success: true,
      data: data,
    };
  } catch (error) {
    console.error("[Booking Service] Error fetching payment response:", error);
    return {
      success: false,
      data: null,
    };
  }
}